import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {localeKeyShort} from '@shared/constants/i18n.constants';
import {MapsService} from '@shared/services/maps.service';
import {Address} from 'ngx-google-places-autocomplete/objects/address';
import {Options} from 'ngx-google-places-autocomplete/objects/options/options';

@Component({
  selector: 'si-maps',
  templateUrl: './maps.component.html',
  styleUrls: ['./maps.component.scss']
})
export class MapsComponent {
  @Input() ngxPlacesInputOptions = {
    componentRestrictions: {country: localeKeyShort.ES}
  } as Options;
  @Input() options: google.maps.MapOptions;
  @Input() mapHeight: number;
  @Input() markerOptions: google.maps.MarkerOptions;
  @Input() markerLabel: string;
  @Input() markerTitle: string;
  @Input() showCenter: boolean;
  @Input() styleClass: string;
  @Output() addressChange = new EventEmitter<Address>();
  @ViewChild('mapContainer') mapContainer: ElementRef;
  markerPosition: google.maps.LatLngLiteral;
  center: google.maps.LatLngLiteral;
  mapWidth: number;
  latitude: number;
  longitude: number;
  private _showPosition = false;
  private _defaultAddress: string;
  private _defaultCoords: [number, number];

  constructor(
    private mapsService: MapsService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
  }

  get showPosition(): boolean {
    return this._showPosition;
  }

  @Input()
  set showPosition(val: boolean) {
    if (!this.mapsService.isGeolocationAvailable()) {
      this._showPosition = false;
      return;
    }

    this._showPosition = val;
  }

  get defaultAddress(): string {
    return this._defaultAddress;
  }

  @Input()
  set defaultAddress(val: string) {
    this._defaultAddress = val;
    if (this._defaultCoords?.[0] && this._defaultCoords?.[1]) {
      return;
    }

    if (this._defaultAddress) {
      // this.getCoords();
      this.getFirstPredictionCoords();
    }
  }

  get defaultCoords(): [number, number] {
    return this._defaultCoords;
  }

  @Input()
  set defaultCoords(val: [number, number]) {
    this._defaultCoords = val;
    if (this._defaultCoords?.[0] && this._defaultCoords?.[1]) {
      this.addMarker(this._defaultCoords[0], this._defaultCoords[1]);
      this.centerMap(this._defaultCoords[0], this._defaultCoords[1]);
    } else {
      console.warn('MapsComponent: defaultCoords input empty or only one coordinate provided.');
    }
  }

  ngAfterViewInit(): void {
    this.mapWidth = this.mapContainer.nativeElement.offsetWidth;
    this.changeDetectorRef.detectChanges();
  }

  addMarker(lat: number, long: number): void {
    this.markerPosition = {
      lat: lat,
      lng: long
    };
    this.latitude = lat;
    this.longitude = long;
  }

  getLocation(): void {
    this.mapsService.getLocation(this.latitude, this.longitude).subscribe(e => {
      if (e.status === 'OK') {
        this.addressChange.emit(e.results[0]);
      }
    });
  }

  async onClickUseCurrentPosition(): Promise<void> {
    await this.mapsService.getPosition().then(pos => {
      this.latitude = pos.lat;
      this.longitude = pos.lng;
    });
    this.addMarker(this.latitude, this.longitude);
    this.centerMap(this.latitude, this.longitude);
    this.getLocation();
  }

  centerMap(lat: number, lng: number): void {
    this.center = {
      lat: lat,
      lng: lng
    };
  }

  onClickCenterMap(): void {
    if (this.latitude && this.longitude) {
      this.centerMap(this.latitude, this.longitude);
    }
  }

  getCoords(): void {
    this.mapsService.getCoords(this.parseAddress(this.defaultAddress)).subscribe(e => {
      if (e.status === 'OK') {
        this.latitude = e.results[0].geometry.location.lat;
        this.longitude = e.results[0].geometry.location.lng;
        this.addMarker(e.results[0].geometry.location.lat, e.results[0].geometry.location.lng);
        this.centerMap(e.results[0].geometry.location.lat, e.results[0].geometry.location.lng);
      }
    });
  }

  async getFirstPredictionCoords(): Promise<void> {
    const address = await this.mapsService.getFirstPrediction(this.defaultAddress, this.ngxPlacesInputOptions);
    if (address?.geometry) {
      this.latitude = address.geometry.location.lat();
      this.longitude = address.geometry.location.lng();
      this.addMarker(this.latitude, this.longitude);
      this.centerMap(this.latitude, this.longitude);
    }
  }

  parseAddress(address: string): string {
    return address.replace(/ /g, '%20');
  }
}
